import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Copy, KeyRound, Link2, ShieldCheck, XCircle,
} from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs.jsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table.jsx";
import { LoadingState, Spinner, EmptyState } from "@/shared/ui/spinner.jsx";
import { toast } from "@/shared/ui/toaster.jsx";
import { formatDateTime } from "@/shared/lib/format.js";
import {
  useSaveWhatsappConfig, useTestWhatsappConnection, useWhatsappConfig, useWhatsappLogs,
} from "@/features/whatsapp/use-whatsapp.js";

/**
 * Configuração da WhatsApp Cloud API.
 *
 * Diferença central em relação ao app antigo: os campos secretos são
 * **somente escrita**. A tela recebe apenas uma máscara (`EAAG…8fZq`) para
 * confirmar que o valor está salvo; deixar o campo em branco mantém o
 * segredo atual. Antes, o token completo era devolvido ao navegador e podia
 * ser lido na aba Network ou por qualquer script da página.
 */
export function WhatsAppMetaPage() {
  const { data: config, isLoading } = useWhatsappConfig();

  // A tela só monta depois que a configuração chega. Assim o formulário
  // inicializa o próprio estado a partir dela, sem precisar de um efeito
  // que chama setState — padrão que causa renderização em cascata.
  if (isLoading || !config) {
    return (
      <AppShell title="WhatsApp Meta API" subtitle="Integração com a WhatsApp Cloud API">
        <LoadingState message="Carregando configuração..." />
      </AppShell>
    );
  }

  return <WhatsAppMetaConfig config={config} />;
}

function WhatsAppMetaConfig({ config }) {
  const salvar = useSaveWhatsappConfig();
  const testar = useTestWhatsappConnection();

  // Os campos de segredo começam vazios de propósito: não temos os valores,
  // apenas as máscaras que o servidor devolve.
  const [form, setForm] = useState(() => ({
    appId: config.appId ?? "",
    wabaId: config.wabaId ?? "",
    phoneNumberId: config.phoneNumberId ?? "",
    displayPhone: config.displayPhone ?? "",
    apiVersion: config.apiVersion ?? "v21.0",
    accessToken: "",
    appSecret: "",
    webhookVerifyToken: "",
  }));
  const [checks, setChecks] = useState(null);

  const set = (campo) => (e) => setForm((atual) => ({ ...atual, [campo]: e.target.value }));

  // URL exclusiva desta clínica — cada cliente tem o próprio app na Meta e
  // cadastra o próprio callback.
  const urlWebhook = `${window.location.origin}${config.webhookPath}`;

  function copiar(texto, rotulo) {
    navigator.clipboard.writeText(texto).then(
      () => toast.success(`${rotulo} copiado.`),
      () => toast.error("Não foi possível copiar."),
    );
  }

  function submeter(evento) {
    evento.preventDefault();
    salvar.mutate(form, { onSuccess: () => setChecks(null) });
  }

  function testarConexao() {
    testar.mutate(undefined, { onSuccess: (resultado) => setChecks(resultado.checks) });
  }

  return (
    <AppShell
      title="WhatsApp Meta API"
      subtitle="Conecte o número oficial da clínica"
      actions={
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to="/integracoes">
            <ArrowLeft className="h-4 w-4" />
            Integrações
          </Link>
        </Button>
      }
    >
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        {/* ---------------------------------------------------- estado */}
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                  config.connected ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
                }`}
              >
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display text-base font-semibold">
                  {config.connected ? "Integração ativa" : "Integração não validada"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {config.updatedAt
                    ? `Última alteração em ${formatDateTime(config.updatedAt)}${config.updatedBy ? ` por ${config.updatedBy}` : ""}`
                    : "Nenhuma credencial salva ainda."}
                </p>
              </div>
            </div>

            <Button onClick={testarConexao} disabled={testar.isPending || !config.configured}>
              {testar.isPending ? <Spinner /> : null}
              Testar conexão
            </Button>
          </CardContent>
        </Card>

        {/* -------------------------------------- resultado das checagens */}
        {checks && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-base">Resultado do teste</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {checks.map((check) => (
                <div
                  key={check.label}
                  className="flex items-start gap-2 rounded-lg border border-border/60 p-3"
                >
                  {check.ok ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  ) : (
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{check.label}</p>
                    <p className="text-xs text-muted-foreground">{check.detail}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="credenciais">
          <TabsList>
            <TabsTrigger value="credenciais">Credenciais</TabsTrigger>
            <TabsTrigger value="webhook">Webhook</TabsTrigger>
            <TabsTrigger value="logs">Histórico</TabsTrigger>
          </TabsList>

          {/* ----------------------------------------------- credenciais */}
          <TabsContent value="credenciais">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-base">Credenciais do Meta Business</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Os campos de segredo ficam cifrados no servidor e não são exibidos de volta.
                  Deixe em branco para manter o valor atual.
                </p>
              </CardHeader>

              <CardContent>
                <form onSubmit={submeter} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Campo label="App ID" id="appId">
                      <Input id="appId" value={form.appId} onChange={set("appId")} placeholder="123456789012345" />
                    </Campo>

                    <Campo label="WABA ID" id="wabaId">
                      <Input id="wabaId" value={form.wabaId} onChange={set("wabaId")} placeholder="987654321098765" />
                    </Campo>

                    <Campo label="Phone Number ID" id="phoneNumberId">
                      <Input
                        id="phoneNumberId"
                        value={form.phoneNumberId}
                        onChange={set("phoneNumberId")}
                        placeholder="555000111222"
                      />
                    </Campo>

                    <Campo label="Número exibido" id="displayPhone">
                      <Input
                        id="displayPhone"
                        value={form.displayPhone}
                        onChange={set("displayPhone")}
                        placeholder="+55 47 98883-2333"
                      />
                    </Campo>

                    <Campo label="Versão da API" id="apiVersion">
                      <Input id="apiVersion" value={form.apiVersion} onChange={set("apiVersion")} placeholder="v21.0" />
                    </Campo>
                  </div>

                  <div className="space-y-4 rounded-xl border border-warning/30 bg-warning/5 p-4">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <KeyRound className="h-4 w-4 text-warning" />
                      Segredos
                    </div>

                    <CampoSecreto
                      label="Access Token"
                      id="accessToken"
                      valor={form.accessToken}
                      onChange={set("accessToken")}
                      mascara={config.accessTokenMask}
                    />
                    <CampoSecreto
                      label="App Secret"
                      id="appSecret"
                      valor={form.appSecret}
                      onChange={set("appSecret")}
                      mascara={config.appSecretMask}
                      ajuda="Usado para conferir a assinatura dos eventos recebidos."
                    />
                    <CampoSecreto
                      label="Webhook Verify Token"
                      id="webhookVerifyToken"
                      valor={form.webhookVerifyToken}
                      onChange={set("webhookVerifyToken")}
                      mascara={config.webhookVerifyTokenMask}
                      ajuda="O mesmo valor informado no painel da Meta ao cadastrar o webhook."
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={salvar.isPending}>
                      {salvar.isPending ? <Spinner /> : null}
                      Salvar credenciais
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* --------------------------------------------------- webhook */}
          <TabsContent value="webhook">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-base">Configuração do webhook</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Cadastre esta URL no SEU app da Meta, em WhatsApp → Configuração → Webhooks.
                </p>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>URL de callback</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={urlWebhook} className="font-mono text-xs" />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => copiar(urlWebhook, "URL")}
                      aria-label="Copiar URL"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Esta URL é exclusiva da sua clínica — o trecho final{" "}
                    <code className="rounded bg-muted px-1">{config.clinicId}</code> identifica o
                    seu ambiente. Não use a URL de outro cliente.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Verify token</Label>
                  <Input
                    readOnly
                    value={config.webhookVerifyTokenMask ?? "— não configurado —"}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Defina o valor na aba Credenciais e informe o mesmo texto no painel da Meta.
                  </p>
                </div>

                <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-xs text-muted-foreground">
                  <p className="mb-1 flex items-center gap-1.5 font-medium text-foreground">
                    <Link2 className="h-3.5 w-3.5" />
                    Sobre a segurança
                  </p>
                  Cada evento é conferido contra a assinatura{" "}
                  <code className="rounded bg-background px-1">X-Hub-Signature-256</code>, calculada
                  com o <strong>App Secret desta clínica</strong> — e só com ele. Requisições sem
                  assinatura válida são recusadas com 401 e ficam registradas no Histórico.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ------------------------------------------------------ logs */}
          <TabsContent value="logs">
            <LogsIntegracao />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function LogsIntegracao() {
  const { data: logs = [], isLoading } = useWhatsappLogs({ limit: 100 });

  const cores = {
    recebida: "secondary",
    enviada: "default",
    erro: "destructive",
    teste: "outline",
    alteracao: "warning",
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-base">Histórico da integração</CardTitle>
        <p className="text-xs text-muted-foreground">Últimos 100 eventos.</p>
      </CardHeader>

      <CardContent className="px-0 pb-0">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-6 w-6 text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={Link2}
            title="Nenhum evento ainda"
            description="Envie ou receba uma mensagem para o histórico começar a aparecer."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead>Quando</TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Responsável</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {formatDateTime(log.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={cores[log.eventType] ?? "outline"} className="text-[10px]">
                      {log.eventType}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {log.contactNumber ? `+${log.contactNumber}` : "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {log.error ? (
                      <span className="text-destructive">{log.error.slice(0, 60)}</span>
                    ) : (
                      (log.status ?? "—")
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {log.userResponsible ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Campo({ label, id, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

/**
 * Campo somente-escrita. Mostra a máscara do valor guardado e aceita um
 * novo — nunca exibe o segredo em si.
 */
function CampoSecreto({ label, id, valor, onChange, mascara, ajuda }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {mascara && (
          <Badge variant="secondary" className="font-mono text-[10px]">
            salvo: {mascara}
          </Badge>
        )}
      </div>
      <Input
        id={id}
        type="password"
        autoComplete="off"
        value={valor}
        onChange={onChange}
        placeholder={mascara ? "Deixe em branco para manter o valor atual" : "Cole o valor aqui"}
      />
      {ajuda && <p className="text-xs text-muted-foreground">{ajuda}</p>}
    </div>
  );
}
